/**
 * BigConnect website widget — the embeddable "talk to us" button.
 *
 * A client pastes one tag onto their own site:
 *
 *   <script type="module" src="https://.../widget/widget.js"
 *           data-widget-key="wk_..."></script>
 *
 * ## Two ways to use it
 *
 * 1. **The floating button** (default). A launcher opens a small panel with Start call.
 *    `data-scenario="hospitality"` makes that button always use one scenario.
 * 2. **The page's own form.** `data-launcher="none"` hides the button, and the page drives
 *    calls through `window.BigConnect` (below), for example from a use-case dropdown:
 *
 *      BigConnect.on('status', (s) => ...);      // idle | connecting | live | ended | error
 *      BigConnect.startCall({ scenario: 'hospitality', visitorName: 'Ama' });
 *      BigConnect.endCall();
 *
 *    `window.BigConnect` exists once the script has run; a `bigconnect:ready` event fires on
 *    `window` at that moment for pages that load it asynchronously.
 *
 * Scenario keys come from the widget's settings in the dashboard. The page only names one;
 * the server looks up what it means, so a page cannot change what the agent is told.
 *
 * ## Design constraints, because this runs on somebody else's website
 *
 * - **Nothing leaks either way.** All markup lives in a shadow root, so the client's CSS
 *   cannot deform the widget and the widget's CSS cannot touch their page.
 * - **Tiny on page load.** This file renders a button and nothing else. The WebRTC engine
 *   (`./call`) is a separate chunk fetched on first call, so a visitor who never calls never
 *   downloads it.
 * - **No cookies, no localStorage, no analytics.** Storing anything would drag our clients
 *   into their own consent-banner problem for no benefit to them.
 * - **No agent id in the page.** The key alone identifies the widget; the server resolves
 *   which agent answers. A key lifted from the source cannot be repointed at another agent.
 * - **Keyboard and screen-reader usable**, since it's public-facing: real buttons, a focus
 *   trap-free dialog that returns focus on close, Escape to close, and a live region so
 *   status changes are announced rather than only shown.
 */
import { WIDGET_CSS } from './styles';
import type { WidgetCall, CaptionLine, CallOptions } from './call';

/** Baked in at build time; `data-api` overrides it for staging or self-hosting. */
const DEFAULT_API_BASE =
  (import.meta as { env?: Record<string, string> }).env?.VITE_API_ENDPOINT
  || 'https://cnoswu9i2i.execute-api.us-east-1.amazonaws.com/dev';

type Phase = 'idle' | 'connecting' | 'live' | 'error';
/** What the page sees. `ended` and `error` are momentary: the next state is `idle`. */
type Status = 'idle' | 'connecting' | 'live' | 'ended' | 'error';

interface WidgetConfig {
  widgetKey: string;
  apiBase: string;
  label: string;
  title: string;
  subtitle: string;
  accent: string;
  position: 'bottom-right' | 'bottom-left';
  launcher: boolean;
  scenario: string;
}

/** Read config from the script tag that loaded us. */
function readConfig(): WidgetConfig | null {
  // `document.currentScript` is null inside an ES module, so find the tag by its attribute.
  const tag = document.querySelector<HTMLScriptElement>('script[data-widget-key]');
  const widgetKey = tag?.dataset.widgetKey?.trim() || '';
  if (!widgetKey) {
    // Fail quietly but say why — a silent no-op would be baffling for whoever pasted it.
    console.warn('[BigConnect] Widget not started: data-widget-key is missing.');
    return null;
  }
  const position = tag?.dataset.position === 'bottom-left' ? 'bottom-left' : 'bottom-right';
  return {
    widgetKey,
    apiBase: (tag?.dataset.api?.trim() || DEFAULT_API_BASE).replace(/\/+$/, ''),
    label: tag?.dataset.label?.trim() || 'Talk to us',
    title: tag?.dataset.title?.trim() || 'Talk to us',
    subtitle: tag?.dataset.subtitle?.trim() || 'Speak to our assistant, free',
    accent: tag?.dataset.color?.trim() || '#003bff',
    position,
    launcher: tag?.dataset.launcher?.trim().toLowerCase() !== 'none',
    scenario: tag?.dataset.scenario?.trim() || '',
  };
}

// ─── The call, shared by the button and the page API ─────────────────────────────

interface ControllerEvents {
  status: (status: Status, detail?: { message?: string }) => void;
  captions: (lines: CaptionLine[]) => void;
}

/**
 * One call at a time, whoever started it. The floating panel and `window.BigConnect` both
 * drive this, so a call started from the page can still be ended from the panel and the
 * reverse.
 */
class CallController {
  private config: WidgetConfig;
  private call: WidgetCall | null = null;
  private listeners: { [K in keyof ControllerEvents]: Set<ControllerEvents[K]> } = {
    status: new Set(),
    captions: new Set(),
  };
  phase: Phase = 'idle';
  muted = false;

  constructor(config: WidgetConfig) {
    this.config = config;
  }

  on<K extends keyof ControllerEvents>(event: K, handler: ControllerEvents[K]): () => void {
    this.listeners[event].add(handler);
    return () => { this.listeners[event].delete(handler); };
  }

  private emit<K extends keyof ControllerEvents>(event: K, ...args: Parameters<ControllerEvents[K]>): void {
    for (const handler of this.listeners[event]) {
      try {
        (handler as (...a: Parameters<ControllerEvents[K]>) => void)(...args);
      } catch (err) {
        // A broken page handler must not break the call.
        console.error('[BigConnect] A status handler failed:', err);
      }
    }
  }

  get status(): Status {
    return this.phase === 'error' ? 'idle' : this.phase;
  }

  async start(options: CallOptions = {}): Promise<void> {
    if (this.phase === 'connecting' || this.phase === 'live') return;
    if (!window.isSecureContext) {
      this.fail('A secure (HTTPS) connection is needed for calls.');
      return;
    }
    this.phase = 'connecting';
    this.muted = false;
    this.emit('status', 'connecting');

    const callOptions: CallOptions = {
      scenario: options.scenario || this.config.scenario || undefined,
      visitorName: options.visitorName,
    };

    try {
      // The heavy WebRTC chunk arrives here, on first call — not on page load.
      const { WidgetCall: Engine } = await import('./call');
      const call = new Engine(this.config.apiBase, this.config.widgetKey, {
        onLive: () => { this.phase = 'live'; this.emit('status', 'live'); },
        onCaptions: (lines) => this.emit('captions', lines),
        onEnded: () => this.finish(),
        onError: (message) => this.fail(message),
      }, callOptions);
      this.call = call;
      await call.start();
    } catch {
      // A failed chunk fetch (offline, blocked) lands here rather than in the engine.
      this.fail('Could not load the calling service. Please try again.');
    }
  }

  end(): void {
    if (this.call) this.call.stop();
    else if (this.phase !== 'idle') this.finish();
  }

  setMuted(muted: boolean): void {
    if (!this.call) return;
    this.muted = muted;
    if (muted) this.call.mute(); else this.call.unmute();
  }

  private finish(): void {
    if (this.phase === 'idle') return;
    this.call = null;
    this.phase = 'idle';
    this.muted = false;
    this.emit('status', 'ended');
    this.emit('status', 'idle');
  }

  private fail(message: string): void {
    this.call = null;
    this.phase = 'idle';
    this.muted = false;
    this.emit('status', 'error', { message });
    this.emit('status', 'idle');
  }
}

/** What `window.BigConnect` offers a page. Kept small on purpose. */
interface PageApi {
  readonly version: string;
  readonly status: Status;
  startCall(options?: CallOptions): Promise<void>;
  endCall(): void;
  mute(): void;
  unmute(): void;
  on(event: 'status', handler: (status: Status, detail?: { message?: string }) => void): () => void;
  on(event: 'captions', handler: (lines: CaptionLine[]) => void): () => void;
}

function exposeApi(controller: CallController): void {
  const api: PageApi = {
    version: '1.1',
    get status() { return controller.status; },
    startCall: (options) => controller.start(options || {}),
    endCall: () => controller.end(),
    mute: () => controller.setMuted(true),
    unmute: () => controller.setMuted(false),
    on: ((event: keyof ControllerEvents, handler: never) => controller.on(event, handler)) as PageApi['on'],
  };
  Object.defineProperty(window, 'BigConnect', { value: Object.freeze(api), configurable: true });
  window.dispatchEvent(new CustomEvent('bigconnect:ready'));
}

// ─── The floating button and panel ───────────────────────────────────────────────

const ICON = {
  phone: '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>',
  hangup: '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67M5 5a16 16 0 0 0 3 5M2 2l20 20"/></svg>',
  mic: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4"/></svg>',
  micOff: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M1 1l22 22M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"/><path d="M17 16.95A7 7 0 0 1 5 12v-2M12 19v4"/></svg>',
  close: '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>',
};

function mountLauncher(config: WidgetConfig, controller: CallController): void {
  const host = document.createElement('div');
  host.setAttribute('data-position', config.position);
  // A generic name: this element is visible in the client's DOM inspector.
  host.setAttribute('data-bigconnect-widget', '');
  const root = host.attachShadow({ mode: 'open' });

  const style = document.createElement('style');
  style.textContent = WIDGET_CSS;
  root.appendChild(style);

  const wrap = document.createElement('div');
  wrap.innerHTML = `
    <button class="launcher" type="button" aria-haspopup="dialog" aria-expanded="false">
      ${ICON.phone}<span>${escapeHtml(config.label)}</span>
    </button>

    <section class="panel" role="dialog" aria-label="${escapeHtml(config.title)}" hidden>
      <div class="head">
        <div>
          <h2>${escapeHtml(config.title)}</h2>
          <p>${escapeHtml(config.subtitle)}</p>
        </div>
        <button class="close" type="button" aria-label="Close">${ICON.close}</button>
      </div>

      <div class="body">
        <p class="note" role="alert" hidden></p>

        <div class="stage-idle">
          <p class="hint">You'll speak to our assistant in your browser. Your microphone is
            used only during the call.</p>
          <div class="row">
            <button class="primary start" type="button">${ICON.phone}<span>Start call</span></button>
          </div>
          <p class="legal">No phone number needed. Calls may be recorded for quality.</p>
        </div>

        <div class="stage-live" hidden>
          <p class="status">
            <span class="dot pulse"></span>
            <span class="state">Connecting…</span>
            <span class="timer"></span>
          </p>
          <div class="row">
            <button class="danger end" type="button">${ICON.hangup}<span>End call</span></button>
            <button class="ghost mute" type="button" aria-pressed="false">${ICON.mic}<span>Mute</span></button>
          </div>
          <div class="captions" hidden aria-live="polite"></div>
        </div>
      </div>
    </section>
  `;
  root.appendChild(wrap);

  // Client-supplied accent, applied as a custom property.
  host.style.setProperty('--bc-accent', config.accent);
  document.body.appendChild(host);

  // ── Element handles ─────────────────────────────────────────────────────────
  const $ = <T extends Element>(sel: string) => root.querySelector<T>(sel)!;
  const launcher = $<HTMLButtonElement>('.launcher');
  const panel = $<HTMLElement>('.panel');
  const closeBtn = $<HTMLButtonElement>('.close');
  const note = $<HTMLParagraphElement>('.note');
  const stageIdle = $<HTMLElement>('.stage-idle');
  const stageLive = $<HTMLElement>('.stage-live');
  const startBtn = $<HTMLButtonElement>('.start');
  const endBtn = $<HTMLButtonElement>('.end');
  const muteBtn = $<HTMLButtonElement>('.mute');
  const stateText = $<HTMLElement>('.state');
  const timerText = $<HTMLElement>('.timer');
  const captions = $<HTMLElement>('.captions');

  let startedAt = 0;
  let timer: number | undefined;

  const setNote = (message: string | null) => {
    note.textContent = message || '';
    note.hidden = !message;
  };

  const renderMute = () => {
    muteBtn.setAttribute('aria-pressed', String(controller.muted));
    muteBtn.innerHTML = controller.muted ? `${ICON.micOff}<span>Unmute</span>` : `${ICON.mic}<span>Mute</span>`;
  };

  const render = () => {
    const phase = controller.phase;
    const live = phase === 'live' || phase === 'connecting';
    stageIdle.hidden = live;
    stageLive.hidden = !live;
    startBtn.disabled = phase === 'connecting';
    stateText.textContent =
      phase === 'connecting' ? 'Connecting…' : phase === 'live' ? 'Connected' : '';
    renderMute();
  };

  const openPanel = () => {
    panel.hidden = false;
    launcher.hidden = true;
    launcher.setAttribute('aria-expanded', 'true');
    // Move focus into the dialog so keyboard users land where the action is.
    (controller.phase === 'idle' ? startBtn : endBtn).focus();
  };

  const closePanel = () => {
    panel.hidden = true;
    launcher.hidden = false;
    launcher.setAttribute('aria-expanded', 'false');
    launcher.focus(); // Return focus where it came from.
  };

  const stopTimer = () => {
    if (timer) window.clearInterval(timer);
    timer = undefined;
    timerText.textContent = '';
  };

  const startTimer = () => {
    startedAt = Date.now();
    stopTimer();
    // Derive from the start time rather than counting ticks, so a throttled background
    // tab doesn't drift.
    timer = window.setInterval(() => {
      const s = Math.floor((Date.now() - startedAt) / 1000);
      timerText.textContent = `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
    }, 1000);
  };

  const clearCaptions = () => {
    captions.hidden = true;
    captions.textContent = '';
  };

  controller.on('status', (status, detail) => {
    if (status === 'connecting') { setNote(null); clearCaptions(); }
    if (status === 'live') startTimer();
    if (status === 'ended' || status === 'idle') stopTimer();
    if (status === 'error') { stopTimer(); setNote(detail?.message || 'The call could not be started.'); }
    render();
  });

  controller.on('captions', (lines) => {
    if (lines.length === 0) return;
    captions.hidden = false;
    captions.innerHTML = lines
      .map((l) => {
        const who = l.role === 'agent' ? 'agent' : 'you';
        const label = l.role === 'agent' ? 'Assistant' : 'You';
        return `<p class="line"><span class="who ${who}">${label}:</span> <span class="said">${escapeHtml(l.content)}</span></p>`;
      })
      .join('');
    captions.scrollTop = captions.scrollHeight;
  });

  launcher.addEventListener('click', openPanel);
  closeBtn.addEventListener('click', () => {
    // Closing the panel must not silently abandon a live call still using the microphone.
    controller.end();
    closePanel();
  });
  startBtn.addEventListener('click', () => { void controller.start(); });
  endBtn.addEventListener('click', () => controller.end());
  muteBtn.addEventListener('click', () => {
    controller.setMuted(!controller.muted);
    renderMute();
  });

  root.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Escape' && !panel.hidden) closePanel();
  });

  render();
}

/** Escape interpolated text — client-supplied labels and agent speech both land in HTML. */
function escapeHtml(value: string): string {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function boot(): void {
  // Guard against the tag being pasted twice on one page.
  if ((window as unknown as { BigConnect?: unknown }).BigConnect) return;
  const config = readConfig();
  if (!config) return;

  // Calls need a secure context for microphone access. Say so in the console rather than
  // rendering a button that cannot work. The API still loads, so a page's own form gets a
  // clear error from startCall instead of a missing object.
  if (!window.isSecureContext) {
    console.warn('[BigConnect] Widget needs HTTPS (or localhost) for microphone access.');
  }

  const controller = new CallController(config);
  // Never leave a microphone open because the visitor navigated away.
  window.addEventListener('pagehide', () => controller.end());

  exposeApi(controller);
  if (config.launcher && window.isSecureContext && !document.querySelector('[data-bigconnect-widget]')) {
    mountLauncher(config, controller);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot, { once: true });
} else {
  boot();
}
